"use client";

import { TRENDING_NEWS_API } from "@/lib/constants";
import { TrendingNews } from "@prisma/client";
import { useQuery } from "@tanstack/react-query";
import Lottie from "lottie-react";
import { ChevronLeftIcon, ChevronRightIcon, TrendingUpIcon } from "lucide-react";
import { useState } from "react";
import trendingAnimation from "./trending.json";

async function getTrendingNewsData() {
	const res = await fetch(TRENDING_NEWS_API, { next: { revalidate: 60 } });
	const { data } = (await res.json()) as { data: TrendingNews[] };

	return data;
}

export const TrendingNewsCard = () => {
	const [currentRegionIndex, setCurrentRegionIndex] = useState(0);
	const {
		isLoading,
		isError,
		data: trendingNews = [],
	} = useQuery({
		queryKey: ["trendingNews"],
		queryFn: getTrendingNewsData,
		refetchOnWindowFocus: false,
	});

	if (isLoading) {
		return (
			<div className="relative rounded-lg bg-white">
				<div className="flex items-center justify-center py-24">
					<p className="text-sm text-gray-600">Loading trending news...</p>
				</div>
			</div>
		);
	}

	const regions = trendingNews.map((news) => news.region);
	const uniqueRegions = Array.from(new Set(regions));

	function sortGlobalToFirst(a: string | null, b: string | null) {
		if (a === "Global") {
			return -1;
		} else if (b === "Global") {
			return 1;
		} else {
			return 0;
		}
	}

	uniqueRegions.sort(sortGlobalToFirst);

	const currentTrendingNews = trendingNews.filter(
		(news) => news.region === uniqueRegions[currentRegionIndex]
	);

	const handleShowPreviousPageNews = () => {
		if (currentRegionIndex > 0) {
			setCurrentRegionIndex((prev) => prev - 1);
		} else {
			setCurrentRegionIndex(uniqueRegions.length - 1);
		}
	};

	const handleShowNextPageNews = () => {
		if (currentRegionIndex < uniqueRegions.length - 1) {
			setCurrentRegionIndex((prev) => prev + 1);
		} else {
			setCurrentRegionIndex(0);
		}
	};

	const handleTrackClickEvent = (target_url: string, link_text: string) => {
		window.rudderanalytics?.track("click", {
			target_url,
			link_text,
		});
	};

	return (
		<div className="relative rounded-lg bg-white">
			<div className="flex justify-between p-4">
				<div className="flex items-center gap-1">
					<Lottie animationData={trendingAnimation} loop={true} className="h-6 w-6" />
					<h4 className="font-semibold uppercase text-gray-700">Trending</h4>
				</div>

				<div className="flex items-center gap-1">
					<button onClick={handleShowPreviousPageNews}>
						<ChevronLeftIcon className="h-4 w-4" />
					</button>
					<span className="whitespace-nowrap text-center text-xs">
						{uniqueRegions[currentRegionIndex]}
					</span>

					<button onClick={handleShowNextPageNews}>
						<ChevronRightIcon className="h-4 w-4" />
					</button>
				</div>
			</div>

			<div className="trending-card flex h-36 flex-col overflow-y-scroll">
				{isError ? (
					<div className="mx-4 rounded-md border border-red-500/20 bg-red-500/10 p-4 text-sm font-medium text-red-500">
						Something went wrong while fetching trending news
					</div>
				) : currentTrendingNews?.length > 0 ? (
					currentTrendingNews?.map((news) => (
						<div
							key={news.id}
							className="flex items-center gap-4 border-t border-t-gray-100 px-4 py-3"
						>
							<div>
								<TrendingUpIcon className="h-6 w-6 text-brand-primary" />
							</div>
							<div
								onClick={() =>
									handleTrackClickEvent(news.text ?? "Trending News", news.text ?? "Trending News")
								}
								className="customized-scrollbar trending-text text-xs text-gray-500"
								dangerouslySetInnerHTML={{ __html: news.text ?? "" }}
							/>
						</div>
					))
				) : (
					<div className="mx-4 rounded-md border border-brand-primary/20 bg-brand-primary/10 p-4 text-sm font-medium text-brand-primary">
						No news to show
					</div>
				)}
			</div>
		</div>
	);
};
